import { Box, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { Context } from "components/CMS/useStore";
import printy6ImageLoader from "lib/printy6ImageLoader";
import Image from "next/future/image";
import * as React from "react";

type Props = {
  size?: "SMALL" | "LARGE";
  id: number;
  withLink?: boolean;
};

export const Color = styled("div")(({ theme }) => ({
  width: 16,
  height: 16,
  display: "inline-block",
  border: "1px solid",
  borderColor: theme.palette.divider,
  borderRadius: 2,
  marginLeft: 2,
  marginRight: 2,
  "&:after": {
    content: "''",
  },
}));

export const Tag = styled("div")(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 16,
  // paddingLeft: theme.spacing(1),
  // paddingRight: theme.spacing(1),
  padding: `${theme.spacing(0.5)} ${theme.spacing(2)}`,
  color: "white",
  background: grey[900],
}));

const ImageWrap = ({
  src,
  alt,
  size = 800,
}: {
  src: string;
  alt: string;
  size: number;
}) => {
  return (
    <Box sx={{ position: "relative", bgcolor: "#f2f2f2" }}>
      <Box sx={{ width: "100%", pb: "100%" }}></Box>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          pb: "100%",
          fontSize: 0,
          "& img": {
            width: "100%",
          },
          "&:after": {
            content: "''",
            width: "100%",
            paddingBottom: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 2,
            background: "rgb(0,0,0, 0.02)",
          },
        }}
      >
        {src && (
          <Image
            src={printy6ImageLoader({ src, quality: size })}
            width={size}
            height={size}
            alt={alt}
            loading="lazy"
            unoptimized={true}
          />
        )}
      </Box>
    </Box>
  );
};

export default function ProductCard({
  id,
  size = "LARGE",
  withLink = true,
}: Props): React.ReactElement {
  const { fetchData } = React.useContext<any>(Context);
  const { templates: data } = fetchData;

  const template = React.useMemo(() => {
    return data?.find((item) => item.id === id);
  }, []);

  const isSmall = React.useMemo(() => {
    return size === "SMALL";
  }, [size]);

  if (!withLink) {
    return <Card isSmall={isSmall} template={template} />;
  }

  return (
    <a
      href={`https://www.printy6.com/design/${template.slug}`}
      className="no-underline"
    >
      <Card isSmall={isSmall} template={template} />
    </a>
  );
}

function Card({ isSmall, template }) {
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: (theme) => theme.palette.divider,
        height: "100%",
        cursor: "pointer",
        borderRadius: isSmall ? 1 : 2,
        overflow: "hidden",
        // border: "1px solid",
        // borderColor: (theme) => theme.palette.divider,
        "&:hover": {
          border: "0px",
          boxShadow: (theme) => theme.shadows[6],
          "& .favorite-button": {
            display: "block",
          },
        },
      }}
    >
      <Box
        sx={{
          fontSize: 0,
          position: "relative",
          "&:hover": {
            "& .template-list-style-image": {
              opacity: 1,
              transition: (theme) =>
                theme.transitions.create("opacity", {
                  duration: 500,
                  delay: 50,
                }),
            },
          },
        }}
      >
        <Box
          sx={{
            zIndex: 1,
            // borderRadius: "8px 8px 0 0 ",
            overflow: "hidden",
          }}
        >
          <ImageWrap
            src={template.first_image_src}
            alt={template.title}
            size={800}
          />
        </Box>
        {template.secondary_image_src && (
          <Box
            className="template-list-style-image"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 2,
              opacity: 0,
              width: "100%",
              height: "100%",
              overflow: "hidden",
              borderRadius: "8px 8px 0 0 ",
              transition: (theme) =>
                theme.transitions.create("opacity", {
                  duration: 500,
                  delay: 0,
                }),
            }}
          >
            <ImageWrap
              src={template.secondary_image_src}
              alt={template.title}
              size={800}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          px: {
            xs: 1.5,
            sm: 1.5,
            md: 1.5,
            lg: 2,
            xl: 2,
          },
          pt: 1,
          pb: 2,
        }}
      >
        <Typography
          variant={isSmall ? "body2" : "body1"}
          component="div"
          color="textPrimary"
          sx={{
            minHeight: isSmall ? 32 : 48,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            mt: isSmall ? 0 : 0.5,
          }}
        >
          {template.title}
        </Typography>
      </Box>
    </Box>
  );
}
